import React, { useState, useRef, useEffect, useContext, memo } from "react";
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  IconButton,
  Select,
  Text,
  Textarea,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { ChatIcon } from "@chakra-ui/icons";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { invokeAgentChat } from "../../api/agentcore";
import { LoginUserContext } from "../../providers/LoginUserProvider";

interface Message {
  role: "user" | "assistant";
  content: string;
}

const STORAGE_KEY = "ai_chat_messages";

export const ChatDrawer: React.FC = memo(() => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { token } = useContext(LoginUserContext);
  const [messages, setMessages] = useState<Message[]>(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]"); } catch { return []; }
  });
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [model, setModel] = useState("claude-sonnet-4-6");
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);
  useEffect(() => { localStorage.setItem(STORAGE_KEY, JSON.stringify(messages.slice(-30))); }, [messages]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading || !token) return;
    setInput("");
    setError("");
    setMessages((prev) => [...prev, { role: "user", content: text }]);
    setLoading(true);
    try {
      const response = await invokeAgentChat(text, token, model);
      setMessages((prev) => [...prev, { role: "assistant", content: response }]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "エラーが発生しました");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  return (
    <>
      <IconButton
        aria-label="AIチャット"
        icon={<ChatIcon />}
        position="fixed"
        bottom="24px"
        right="24px"
        size="lg"
        colorScheme="blue"
        borderRadius="full"
        boxShadow="lg"
        onClick={onOpen}
        zIndex={1000}
      />
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">
            <Flex align="center" gap={2}>
              <Text fontSize="md" fontWeight="bold">AIチャット</Text>
              <Select size="xs" w="160px" value={model} onChange={(e) => setModel(e.target.value)}>
                <option value="claude-sonnet-4-6">Sonnet 4.6</option>
                <option value="claude-haiku-4-5">Haiku 4.5（高速）</option>
                <option value="nova-pro">Nova Pro</option>
                <option value="nova-lite">Nova Lite（最速）</option>
              </Select>
              <Button size="xs" variant="outline" onClick={() => { setMessages([]); localStorage.removeItem(STORAGE_KEY); }}>
                クリア
              </Button>
            </Flex>
          </DrawerHeader>
          <DrawerBody display="flex" flexDirection="column" p={0}>
            <VStack flex={1} overflowY="auto" spacing={3} p={4} align="stretch">
              {messages.length === 0 && !loading && (
                <Text color="gray.500" textAlign="center" mt={8} fontSize="sm">
                  日次処理の確認・変更を自然言語で指示できます。
                  <br />例: 「digiparkのスケジュールを確認して」
                </Text>
              )}
              {messages.map((msg, i) => (
                <Box
                  key={i}
                  alignSelf={msg.role === "user" ? "flex-end" : "flex-start"}
                  bg={msg.role === "user" ? "blue.50" : "gray.50"}
                  px={3} py={2} borderRadius="md" maxW="90%" fontSize="sm"
                >
                  <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={1}>
                    {msg.role === "user" ? "あなた" : "AI"}
                  </Text>
                  {msg.role === "assistant" ? (
                    <Box sx={{ "& table": { fontSize: "xs", width: "100%" }, "& pre": { fontSize: "xs", overflowX: "auto" } }}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                    </Box>
                  ) : (
                    <Text whiteSpace="pre-wrap">{msg.content}</Text>
                  )}
                </Box>
              ))}
              {loading && (
                <Box alignSelf="flex-start" bg="gray.50" px={3} py={2} borderRadius="md" fontSize="sm">
                  <Text color="gray.500">⏳ 回答を生成中...</Text>
                </Box>
              )}
              {error && <Text color="red.500" fontSize="xs">⚠ {error}</Text>}
              <div ref={bottomRef} />
            </VStack>
            <Flex p={3} borderTopWidth="1px" gap={2}>
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="メッセージを入力（Enterで送信）"
                size="sm"
                rows={2}
                resize="none"
                disabled={loading}
              />
              <Button
                colorScheme="blue"
                size="sm"
                alignSelf="flex-end"
                onClick={handleSend}
                isDisabled={!input.trim() || loading}
                isLoading={loading}
              >
                送信
              </Button>
            </Flex>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
});

ChatDrawer.displayName = "ChatDrawer";
